import dayjs from "dayjs";
import type { CitacaoPorMotor, RoboDeIa, SemanaDeVisitas } from "../../../services/presenca-ia";

const NOMES_MOTOR: Record<string, string> = {
  openai: "ChatGPT (OpenAI)",
  perplexity: "Perplexity",
};

export const nomeDoMotor = (motor: string) => NOMES_MOTOR[motor] || motor;

export const dataCurta = (valor: string | null | undefined) => (valor ? dayjs(valor).format("DD/MM/YYYY") : "—");

/** O robô que leu o /llms.txt primeiro: é o momento em que "a IA achou a página". */
export function primeiraLeituraDoLlms(robos: RoboDeIa[]): RoboDeIa | null {
  const leram = robos.filter((r) => r.leuLlms && r.primeiraLeituraLlms);
  if (!leram.length) return null;
  return leram.reduce((a, b) => (dayjs(b.primeiraLeituraLlms).isBefore(dayjs(a.primeiraLeituraLlms)) ? b : a));
}

/** Semana mais recente de cada motor. */
export function ultimaSemanaPorMotor(citacoes: CitacaoPorMotor[]): CitacaoPorMotor[] {
  const porMotor = new Map<string, CitacaoPorMotor>();
  for (const c of citacoes) {
    const atual = porMotor.get(c.motor);
    if (!atual || c.semana > atual.semana) porMotor.set(c.motor, c);
  }
  return Array.from(porMotor.values()).sort((a, b) => a.motor.localeCompare(b.motor));
}

export type LinhaDaSemana = {
  semana: string;
  visitas: number;
  percentual: number | null;
  percentualControle: number | null;
  indicou: number | null;
  indicouControle: number | null;
};

const somaPercentual = (
  lista: CitacaoPorMotor[],
  campo: "citou" | "recomendou" = "citou",
): number | null => {
  const perguntas = lista.reduce((s, c) => s + c.perguntas, 0);
  if (!perguntas) return null;
  return Math.round((lista.reduce((s, c) => s + (c[campo] ?? 0), 0) * 100) / perguntas);
};

/** Pergunta pelo nome, somando os motores: quantas respostas trouxeram o negócio. */
export function conheceONegocio(citacoes: CitacaoPorMotor[]): { perguntas: number; citou: number } {
  return citacoes.reduce(
    (s, c) => ({ perguntas: s.perguntas + (c.marcaPerguntas ?? 0), citou: s.citou + (c.marcaCitou ?? 0) }),
    { perguntas: 0, citou: 0 },
  );
}

/** Perguntas sobre os serviços do cliente, somando os motores (fora da comparação com o controle). */
export function servicoDoCliente(citacoes: CitacaoPorMotor[]): { perguntas: number; recomendou: number } {
  return citacoes.reduce(
    (s, c) => ({
      perguntas: s.perguntas + (c.servicoPerguntas ?? 0),
      recomendou: s.recomendou + (c.servicoRecomendou ?? 0),
    }),
    { perguntas: 0, recomendou: 0 },
  );
}

/** Uma linha por semana: visitas de robôs, % de citação e de indicação (motores somados) × controle. */
export function evolucaoSemanal(
  porSemana: SemanaDeVisitas[],
  citacoes: CitacaoPorMotor[],
  controle: CitacaoPorMotor[],
): LinhaDaSemana[] {
  const semanas = new Set<string>([
    ...porSemana.map((s) => s.semana),
    ...citacoes.map((c) => c.semana),
  ]);
  return Array.from(semanas)
    .sort()
    .map((semana) => ({
      semana,
      visitas: porSemana.find((s) => s.semana === semana)?.visitas ?? 0,
      percentual: somaPercentual(citacoes.filter((c) => c.semana === semana)),
      percentualControle: somaPercentual(controle.filter((c) => c.semana === semana)),
      indicou: somaPercentual(citacoes.filter((c) => c.semana === semana), "recomendou"),
      indicouControle: somaPercentual(controle.filter((c) => c.semana === semana), "recomendou"),
    }));
}
